import { GenData, IGenMulti, IGenSingle, QMulti, QSingle } from '@educraft/base';
import { PdfPoint, PdfSize } from '../pdf/pdf';

/**
 * Calculate grid origin and cell size for a N×M layout on a page.
 * Equivalent to C# G<T>.CalcOrigin().
 */
export function calcOrigin(
  cols: number,
  rows: number,
  margin: number,
  pageSize: PdfSize
): { origin: PdfPoint; size: PdfSize } {
  const w = (pageSize.width - 2 * margin) / cols;
  const h = (pageSize.height - 2 * margin) / rows;
  return {
    origin: new PdfPoint(
      margin + (pageSize.width - 2 * margin - w * cols) / 2,
      margin + (pageSize.height - 2 * margin - h * rows) / 2
    ),
    size: new PdfSize(w, h),
  };
}

/**
 * Generate N items from an IGenMulti generator with optional uniqueness constraints.
 * Equivalent to C# GUtil.Gen(G<OptsMulti>, ...).
 */
export function genMulti(
  data: GenData,
  generator: IGenMulti,
  count: number,
  questionUnique = false,
  answerUnique = false
): QMulti[] {
  const list: QMulti[] = [];
  const questionHash = new Set<string>();
  const answerHash = new Set<string>();
  let tries = 0;

  while (list.length < count) {
    const q = generator.generate(data);
    const question = q.texts.join(' ');
    const answer = q.answers[q.answerIndex];

    if (tries++ > 10_000) {
      throw new Error('genMulti: exceeded max retries for unique generation');
    }

    if (questionUnique && questionHash.has(question)) continue;
    if (answerUnique && answerHash.has(answer)) continue;

    if (questionUnique) questionHash.add(question);
    if (answerUnique) answerHash.add(answer);

    list.push(q);
    tries = 0;
  }

  return list;
}

/**
 * Generate N items from an IGenSingle generator with optional uniqueness constraints.
 * Equivalent to C# GUtil.Gen(G<OptsSingle>, ...).
 */
export function genSingle(
  data: GenData,
  generator: IGenSingle,
  count: number,
  questionUnique = false,
  answerUnique = false
): QSingle[] {
  const list: QSingle[] = [];
  const questionHash = new Set<string>();
  const answerHash = new Set<string>();
  let tries = 0;

  while (list.length < count) {
    const q = generator.generate(data);
    const question = q.texts.join(' ');
    const {answer} = q;

    if (tries++ > 10_000) {
      throw new Error('genSingle: exceeded max retries for unique generation');
    }

    if (questionUnique && questionHash.has(question)) continue;
    if (answerUnique && answerHash.has(answer)) continue;

    if (questionUnique) questionHash.add(question);
    if (answerUnique) answerHash.add(answer);

    list.push(q);
    tries = 0;
  }

  return list;
}

export function fixGenArray(texts: string[]): string[] {
  if (texts.length === 1 && texts[0].endsWith('=')) {
    const cleaned = texts[0].replace(/=+\s*$/, '').trimEnd();
    return [cleaned];
  }

  return texts;
}
